import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import CompanyTitle from './CompanyTitle';
import CompanySearchBox from './CompanySearchBox';
import CompanyLists from './CompanyLists';
import UpdateCompanyDialog from './UpdateCompanyDialog';
import CreateCompanyDialog from './CreateCompanyDialog';
import UserPrompt from '../UserPrompt';
import LocationSearch from '../LocationSearch';
import {
  updateCompanies,
  deleteWorkRecords,
  getGeocode,
} from '../../actions/api/apis';
import createCompany from '../../actions/api/company/createCompany';
import config from '../../config';
import * as types from '../../lib/constants';

const style = {
  main: {
    flex: '1 0 auto',
    width: '100vw',
    height: '100%',
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: 15,
  },
  block: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
};

const ListsWrapper = styled.div`
  height: ${props => `calc(100% - ${(props.searchBoxHeight || 0) + 51}px)`};
  @media (max-height: 530px) {
    height: ${props => `calc(100% - ${(props.searchBoxHeight || 0) + 31}px)`};
  }
`;

const { searchCompany, searchCompanyLocation } =
  config.customRole.functionAccess;

const CompanyPage = ({
  searchBoxHeight,
  role,
  apiToken,
  companies,
  openUpdateCompanyDialog,
  openCreateCompanyDialog,
  companyTab,
  onClose,
  currentCompanyInfo,
  confirmData,
  confirmDataError,
  openLocationSearchMap,
  searchOptions,
  checkedLicenseOver,
  licenseCapacityStatus,
  dispatch,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const displaySearchOption = searchCompany.indexOf(role) !== -1;
  const displaySearchAddress = searchCompanyLocation.indexOf(role) !== -1;
  const companyTypes = types.COMPANY_TYPES;
  return (
    <div style={style.main} className="companyPage">
      <div style={style.block}>
        <CompanyTitle
          role={role}
          onCreateCompany={() => {
            dispatch({ type: 'OPEN_CREATE_COMPANY_DIALOG' });
          }}
          displaySearchOption={displaySearchOption}
          searchOptions={searchOptions}
          companyTypes={companyTypes}
        />
        {displaySearchOption && (
          <CompanySearchBox
            role={role}
            searchOptions={searchOptions}
            companyTypes={companyTypes}
            onSearch={options =>
              dispatch({ type: 'UPDATE_COMPANY_SEARCH_OPTIONS', options })
            }
            onChange={checked =>
              dispatch({ type: 'CHECKED_LICENSE_OVER', checked })
            }
            checkedLicenseOver={checkedLicenseOver}
            licenseCapacityStatus={licenseCapacityStatus}
          />
        )}
        <ListsWrapper searchBoxHeight={searchBoxHeight}>
          <CompanyLists
            role={role}
            companies={companies}
            onSelect={id => {
              dispatch({ type: 'OPEN_UPDATE_COMPANY_DIALOG', id });
            }}
          />
        </ListsWrapper>
        {openCreateCompanyDialog && (
          <CreateCompanyDialog
            onClose={onClose}
            confirmData={confirmData}
            confirmDataError={confirmDataError}
            onUpdateConfirm={updateParam => {
              dispatch({ type: 'UPDATE_CONFIRM_DATA', updateParam });
            }}
            onCreateCompany={companyCode => {
              dispatch(createCompany(apiToken, companyCode));
            }}
          />
        )}
        {openUpdateCompanyDialog && (
          <UpdateCompanyDialog
            companyTab={companyTab}
            role={role}
            currentCompanyInfo={currentCompanyInfo}
            confirmData={confirmData}
            confirmDataError={confirmDataError}
            onUpdateLicense={(target, updateLicense) => {
              dispatch({ type: 'UPDATE_LICENSE', target, updateLicense });
            }}
            onSelectTab={tab => {
              dispatch({ type: 'UPDATE_COMPANY_SELECT_TAB', tab });
            }}
            onClose={onClose}
            onUpdate={params => {
              dispatch(updateCompanies(apiToken, params));
            }}
            onUpdateConfirm={updateParam => {
              dispatch({ type: 'UPDATE_CONFIRM_DATA', updateParam });
            }}
            onDelete={updateParams => {
              const promptMessage = formatMessage({
                id: 'userPrompt.message.delete',
              });
              const buttonOk = formatMessage({
                id: 'userPrompt.button.delete',
              });
              const buttonNo = formatMessage({
                id: 'userPrompt.button.noDelete',
              });
              const buttonArray = [
                { name: buttonOk, type: 0 },
                { name: buttonNo, type: 1 },
              ];
              const onAccept = ret => {
                if (ret === 0) {
                  dispatch(updateCompanies(apiToken, updateParams));
                }
              };
              onClose();
              dispatch({
                type: 'OPEN_USER_PROMPT',
                promptMessage,
                buttonArray,
                onAccept,
              });
            }}
            onDataDelete={params => {
              const promptMessage = formatMessage({
                id: 'userPrompt.message.dataDelete',
              });
              const buttonOk = formatMessage({
                id: 'userPrompt.button.delete',
              });
              const buttonNo = formatMessage({
                id: 'userPrompt.button.noDelete',
              });
              const buttonArray = [
                { name: buttonOk, type: 0 },
                { name: buttonNo, type: 1 },
              ];
              const onAccept = ret => {
                if (ret === 0) {
                  dispatch(deleteWorkRecords(apiToken, params));
                }
              };
              onClose();
              dispatch({
                type: 'OPEN_USER_PROMPT',
                promptMessage,
                buttonArray,
                onAccept,
              });
            }}
            onSearchLatLng={params =>
              dispatch({ type: 'OPEN_LOCATION_SEARCH', params })
            }
          />
        )}
        {openLocationSearchMap && (
          <LocationSearch
            onChangeAddress={params =>
              dispatch({ type: 'INPUT_SEARCH_LOCATION_SEARCH', params })
            }
            onSearchMap={address => dispatch(getGeocode(apiToken, address))}
            onClickMap={params =>
              dispatch({ type: 'CHANGE_POINT_LOCATION_SEARCH', params })
            }
            onUpdateMap={updateParam => {
              dispatch({
                type: 'SET_GEOCODE_UPDATE_COMPANY_DIALOG',
                updateParam,
              });
              dispatch({ type: 'CLOSE_LOCATION_SEARCH' });
            }}
            onCloseMap={() => dispatch({ type: 'CLOSE_LOCATION_SEARCH' })}
            readOnly={!displaySearchAddress}
          />
        )}
        <UserPrompt />
      </div>
    </div>
  );
};

CompanyPage.propTypes = {
  searchBoxHeight: PropTypes.number.isRequired,
  role: PropTypes.number.isRequired,
  apiToken: PropTypes.string.isRequired,
  currentCompanyInfo: PropTypes.object.isRequired,
  confirmData: PropTypes.object.isRequired,
  confirmDataError: PropTypes.object.isRequired,
  companies: PropTypes.array.isRequired,
  companyTab: PropTypes.number.isRequired,
  openUpdateCompanyDialog: PropTypes.bool.isRequired,
  openCreateCompanyDialog: PropTypes.bool.isRequired,
  openLocationSearchMap: PropTypes.bool.isRequired,
  searchOptions: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  checkedLicenseOver: PropTypes.bool.isRequired,
  licenseCapacityStatus: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default CompanyPage;
